import type { User, UserRole } from '../types';

/**
 * Source of truth for "what RBAC roles does this user hold right now".
 *
 * The `User.roles` field is a deprecated convenience copy that AuthContext
 * keeps in sync with the active org for backward compatibility. New code
 * should always read from `user.orgs.find(o => o.is_active)?.roles` so the
 * answer follows org switches deterministically.
 *
 * IMPORTANT: these helpers cover platform-level RBAC roles ONLY (admin,
 * super_admin) — used for billing/subscription/org-management UI. They do
 * NOT bypass module-licence checks. For module features (timesheet.hr,
 * spray.admin, irrigation.admin, etc.) always use `useModuleLicense`.
 */

export function getActiveOrgRoles(user: User | null | undefined): UserRole[] | undefined {
  return user?.orgs?.find(o => o.is_active)?.roles;
}

export function isOrgAdmin(user: User | null | undefined): boolean {
  const roles = getActiveOrgRoles(user);
  return !!roles?.some(r => r === 'admin' || r === 'super_admin');
}

export function isOrgSuperAdmin(user: User | null | undefined): boolean {
  const roles = getActiveOrgRoles(user);
  return !!roles?.some(r => r === 'super_admin');
}

/**
 * The platform operator's own organisation — us, not a customer. Mirrors
 * `PLATFORM_ORG_ID` in HHNodeServer `src/config/platform.js`.
 */
export const PLATFORM_ORG_ID = 1;

/**
 * Platform billing access: billing_manager or super_admin held in the
 * platform organisation.
 *
 * Billing is ours alone — it is how we give modules to other organisations
 * and control what they hold — so the same role held in a customer org does
 * not count. Unlike the helpers above this is not scoped to the ACTIVE org:
 * someone holding the role in the platform org keeps the way in whichever
 * org their tab is showing. The server (requireBillingAccess) is the real
 * gate; this only decides whether to show the way in.
 */
export function hasBillingAccess(user: User | null | undefined): boolean {
  return !!user?.orgs?.some(
    o => o.id === PLATFORM_ORG_ID && o.roles?.some(r => r === 'billing_manager' || r === 'super_admin'),
  );
}
